import { Injectable } from '@angular/core';
import { AnetCommonService } from '../anet/auth/anet-common.service';
import { HttpClient } from '@angular/common/http';
import { AthleticUser } from '../anet/models/athletic-user.interface';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DeliveryMethod } from '../models/notice/delivery-method.interface';
import { DataContext } from '../../my-meet/models/data-context.model';
import { Notice } from '../models/notice/notice.interface';
import { CreatedEmail } from '../models/notice/created-email.interface';
import { CreatedValidationCode } from '../models/notice/created-validation-code.interface';
import { CreatedPhoneNumber } from '../models/notice/created-phone-number.interface';

@Injectable({
  providedIn: 'root',
})
export class AliveNoticeService {
  constructor(
    private common: AnetCommonService,
    private http: HttpClient,
  ) {}
  async getDeliveryMethods(): Promise<DeliveryMethod[]> {
    return firstValueFrom(
      this.http.get(`${environment.baseAPIEndpoint}/accounts/my-delivery-methods`).pipe(
        map((data: any) => {
          if (!data) return [];
          const obj: { deliveryMethods: DeliveryMethod[] } = data;
          return obj.deliveryMethods;
        }),
      ),
    );
  }

  async getNotices(meetId: number, dataSourceId?: number): Promise<Notice[]> {
    try {
      return firstValueFrom(
        this.http.get(`${environment.baseAPIEndpoint}/notices/my?meetId=` + meetId).pipe(
          catchError((err) => of([])),
          map((data: any) => {
            if (!data) return [];
            const obj: Notice[] = data;
            if (dataSourceId) {
              return obj.filter((item) => {
                return +item.dataSourceId === dataSourceId;
              });
            }
            return obj;
          }),
        ),
      );
    } catch (e) {
      return [];
    }
  }

  async createNotice(deliveryMethod: DeliveryMethod, dataContext: DataContext, meetId: number) {
    return firstValueFrom(
      this.http.post(`${environment.baseAPIEndpoint}/notices`, {
        dataContext: dataContext.dataContext,
        dataSourceId: dataContext.athleticLiveId,
        dataSourceType: dataContext.dataSourceType,
        deliveryContext: deliveryMethod.deliveryContext,
        deliveryMethodId: deliveryMethod.deliveryMethodId,
        deliveryMethodType: deliveryMethod.deliveryMethodType,
        meetId,
      }),
    );
  }

  async deleteNotice(id: string) {
    return firstValueFrom(this.http.delete(`${environment.baseAPIEndpoint}/notices/` + id));
  }

  async getEmail(emailAddress: string): Promise<CreatedEmail> {
    return firstValueFrom(
      this.http
        .get(
          `${environment.baseAPIEndpoint}/emailAddresses/findOneEmailAddress?userEmail=${encodeURIComponent(emailAddress)}`,
        )
        .pipe(
          map((data: any) => {
            const obj: CreatedEmail = data;
            return obj;
          }),
        ),
    );
  }

  async createEmail(emailAddress: string): Promise<CreatedEmail> {
    return firstValueFrom(
      this.http
        .post(`${environment.baseAPIEndpoint}/emailAddresses`, {
          emailAddress,
        })
        .pipe(
          map((data: any) => {
            const obj: CreatedEmail = data;
            return obj;
          }),
        ),
    );
  }

  async getPhoneNumber(phoneNumber: string): Promise<CreatedPhoneNumber> {
    return firstValueFrom(
      this.http
        .get(
          `${environment.baseAPIEndpoint}/phoneNumbers/findOnePhoneNumber?userPhone=${encodeURIComponent(phoneNumber)}`,
        )
        .pipe(
          map((data: any) => {
            const obj: CreatedPhoneNumber = data;
            return obj;
          }),
        ),
    );
  }

  async createPhoneNumber(phoneNumber: string): Promise<CreatedPhoneNumber> {
    return firstValueFrom(
      this.http
        .post(`${environment.baseAPIEndpoint}/phoneNumbers`, {
          phoneNumber,
        })
        .pipe(
          map((data: any) => {
            const obj: CreatedPhoneNumber = data;
            return obj;
          }),
        ),
    );
  }

  async createValidationCode(deliveryMethodId: number, deliveryMethodType: string): Promise<CreatedValidationCode> {
    return firstValueFrom(
      this.http
        .post(`${environment.baseAPIEndpoint}/validationCodes`, {
          deliveryMethodId,
          deliveryMethodType,
        })
        .pipe(
          map((data: any) => {
            const obj: CreatedValidationCode = data;
            return obj;
          }),
        ),
    );
  }

  async validateValidationCode(validationCode: CreatedValidationCode, code: string) {
    return firstValueFrom(
      this.http.get(`${environment.baseAPIEndpoint}/validationCodes/${validationCode.id}/validate?code=${code}`),
    );
  }
}
